
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import MaterialDatabaseExplorer from "../MaterialDatabaseExplorer";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Plus, Trash2, Database, X, Check } from "lucide-react";
import { createNormalizedEpd } from "../../lca/lcaTypes";
import { formatProperty } from "../../utils/okobaudatProperties";

const WallBuildupsContext = createContext(null);

export function WallBuildupsProvider({ children }) {
  const [savedWalls, setSavedWalls] = useState([]);

  const upsertSavedWall = (wall) => {
    if (!wall || !wall.id) return;
    setSavedWalls((prev) => {
      const existing = (prev || []).find((item) => item.id === wall.id) || null;
      const createdAt = existing?.createdAt ?? wall.createdAt ?? wall.savedAt ?? Date.now();
      const nextWall = { ...wall, createdAt };
      if (!existing) {
        return [...(prev || []), nextWall];
      }
      return (prev || []).map((item) => (item.id === wall.id ? nextWall : item));
    });
  };

  const removeSavedWall = (id) => {
    if (!id) return;
    setSavedWalls((prev) => (prev || []).filter((item) => item.id !== id));
  };

  const clearSavedWalls = () => {
    setSavedWalls([]);
  };

  const value = useMemo(
    () => ({ savedWalls, upsertSavedWall, removeSavedWall, clearSavedWalls }),
    [savedWalls]
  );

  return (
    <WallBuildupsContext.Provider value={value}>
      {children}
    </WallBuildupsContext.Provider>
  );
}

export function useWallBuildups() {
  const context = useContext(WallBuildupsContext);
  if (!context) {
    throw new Error("useWallBuildups must be used within WallBuildupsProvider");
  }
  return context;
}

/*
Mapping notes for shortlisted EPD payloads:
- id: raw.id || raw.uuid || raw.raw?.id || raw.refId
- name: raw.name || raw.displayName || raw.product_name || raw.title || raw.raw?.name
- source: raw.source || raw.kind || raw.provider || raw.db || "shortlist"
- declaredUnit: raw.declaredUnit.unit || raw.declaredUnit || raw.declared_unit || raw.unit || raw.referenceUnit || raw.raw?.declared_unit
- stages: prefer raw.modules (keys like A1-A3, A4, A5, B4, C1, C2, C3, C4, D); if absent, fall back to raw.raw?.impacts (OpenEPD detail) by picking the first available method/impact and reading stage.mean values
- C1C4: if a combined key (C1C4, C1-C4) exists, use it; otherwise sum C1 + C2 + C3 + C4 from the available stage entries
*/
export function mapRawEpdToNormalized(raw) {
  const safeNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeDeclaredUnit = (du) => {
    const rawUnit = typeof du === "string" ? du : du?.unit || du?.name || du?.label || du?.text || du?.value || "";
    const u = (rawUnit || "").toString().trim().toLowerCase();

    if (u === "m2" || u === "m\u00b2" || u === "mâ\u201a\u00b2" || u === "ma\u0131") return "m2";
    if (u === "m3" || u === "m\u00b3" || u === "mâ\u201a\u00b3" || u === "ma3") return "m3";
    if (u === "piece" || u === "pcs") return "pcs";
    if (u === "kg") return "kg";

    // Default to kg if unknown
    return "kg";
  };

  const declaredUnitRaw =
    raw?.declaredUnit ??
    raw?.declared_unit ??
    raw?.unit ??
    raw?.referenceUnit ??
    raw?.raw?.declared_unit ??
    raw?.raw?.declaredUnit ??
    null;
  const declaredUnit = normalizeDeclaredUnit(declaredUnitRaw);

  const declaredUnitMeta = (() => {
    if (declaredUnitRaw && typeof declaredUnitRaw === "object") {
      const unit =
        declaredUnitRaw.unit ??
        declaredUnitRaw.name ??
        declaredUnitRaw.label ??
        declaredUnitRaw.text ??
        declaredUnitRaw.value ??
        null;
      const amount = Number(declaredUnitRaw.amount ?? declaredUnitRaw.quantity ?? 1);
      if (!unit) return null;
      return {
        unit: unit.toString(),
        amount: Number.isFinite(amount) ? amount : 1
      };
    }
    if (typeof declaredUnitRaw === "string" && declaredUnitRaw.trim()) {
      return { unit: declaredUnitRaw.trim(), amount: 1 };
    }
    return null;
  })();

  const modulesRaw =
    raw?.modules ||
    raw?.gwp?.modules ||
    raw?.raw?.modules ||
    null;

  const modules = (() => {
    if (!modulesRaw) return null;
    if (Array.isArray(modulesRaw)) {
      const mapped = {};
      modulesRaw.forEach((entry) => {
        const key = entry?.key ?? entry?.module ?? entry?.name ?? "";
        if (!String(key).trim()) return;
        const value = entry?.value ?? entry?.total ?? entry?.mean ?? entry?.amount ?? 0;
        mapped[key] = value;
      });
      return Object.keys(mapped).length ? mapped : null;
    }
    if (typeof modulesRaw === "object") return modulesRaw;
    return null;
  })();

  const pickFromImpacts = () => {
    const impacts = raw?.raw?.impacts || raw?.impacts;
    if (!impacts || typeof impacts !== "object") return null;
    const methodKeys = Object.keys(impacts);
    if (!methodKeys.length) return null;
    const preferredMethods = ["TRACI", "EF", "IPCC"];
    const methodKey =
      methodKeys.find((m) => preferredMethods.some((p) => m.toLowerCase().includes(p.toLowerCase()))) ||
      methodKeys[0];
    const impactNode = impacts[methodKey];
    if (!impactNode || typeof impactNode !== "object") return null;
    const impactKeys = Object.keys(impactNode);
    if (!impactKeys.length) return null;
    const impactKey =
      impactKeys.find((k) => k.toLowerCase() === "gwp") ||
      impactKeys.find((k) => k.toLowerCase().includes("gwp")) ||
      impactKeys[0];
    const stageNode = impactNode[impactKey];
    if (!stageNode || typeof stageNode !== "object") return null;
    return stageNode;
  };

  const stageSource = modules || pickFromImpacts() || {};

  const readStage = (keys) => {
    for (const key of keys) {
      const v = stageSource?.[key];
      if (v == null) continue;
      if (typeof v === "number") return v;
      if (typeof v === "object") {
        const candidate = v.mean ?? v.total ?? v.value ?? v.amount ?? v.base ?? v.calc ?? null;
        if (candidate != null) return candidate;
      }
    }
    return null;
  };

  const a1 = safeNumber(readStage(["A1"]));
  const a2 = safeNumber(readStage(["A2"]));
  const a3 = safeNumber(readStage(["A3"]));
  const directA1A3 = readStage(["A1A3", "A1-A3", "A1A2A3", "A1A2A3A4"]);
  const a1a3 = directA1A3 != null ? safeNumber(directA1A3) : safeNumber(a1 + a2 + a3);

  const a4 = safeNumber(readStage(["A4"]));
  const a5 = safeNumber(readStage(["A5"]));
  const b4 = safeNumber(readStage(["B4"]));

  const c1 = safeNumber(readStage(["C1"]));
  const c2 = safeNumber(readStage(["C2"]));
  const c3 = safeNumber(readStage(["C3"]));
  const c4 = safeNumber(readStage(["C4"]));
  const directC1C4 = readStage(["C1C4", "C1-C4", "C1-4"]);
  const c1c4 = directC1C4 != null ? safeNumber(directC1C4) : safeNumber(c1 + c2 + c3 + c4);

  const d = safeNumber(readStage(["D"]));

  const id = raw?.id ?? raw?.uuid ?? raw?.raw?.id ?? raw?.epdId ?? raw?.refId;
  const uuid =
    raw?.uuid ??
    raw?.raw?.uuid ??
    (raw?.source === "openepd" || raw?.kind === "openepd"
      ? `openepd:${raw?.kind || raw?.type || "product"}:${raw?.id}`
      : null) ??
    (raw?.source === "epd-international" || raw?.provider === "epd-international"
      ? `epd-international:${raw?.registrationNumber || raw?.id || raw?.refId}`
      : null) ??
    (typeof id === "string" && id.includes(":") ? id : null);
  const name =
    raw?.name ??
    raw?.displayName ??
    raw?.product_name ??
    raw?.title ??
    raw?.raw?.name ??
    raw?.raw?.product_name ??
    "EPD";
  const source = raw?.source ?? raw?.kind ?? raw?.provider ?? raw?.db ?? "shortlist";
  const geography =
    raw?.geography ??
    raw?.location ??
    raw?.country ??
    raw?.raw?.geography ??
    raw?.raw?.location ??
    raw?.raw?.country ??
    null;

  const materialProperties =
    raw?.materialProperties ||
    raw?.raw?.materialProperties ||
    raw?.properties ||
    raw?.raw?.properties ||
    null;

  const fallbackModules = (() => {
    const next = {};
    const add = (key, value) => {
      if (value == null) return;
      if (!Number.isFinite(Number(value))) return;
      next[key] = Number(value);
    };
    add("A1", a1);
    add("A2", a2);
    add("A3", a3);
    add("A1-A3", a1a3);
    add("A4", a4);
    add("A5", a5);
    add("B4", b4);
    add("C1", c1);
    add("C2", c2);
    add("C3", c3);
    add("C4", c4);
    add("C1-C4", c1c4);
    add("D", d);
    return Object.keys(next).length ? next : null;
  })();

  const normalized = createNormalizedEpd({
    id,
    name,
    source,
    declaredUnit,
    geography,
    gwp: {
      A1A3: a1a3,
      A4: a4,
      A5: a5,
      B4: b4,
      C1C4: c1c4,
      D: d
    }
  });

  const gwpTotal = (() => {
    const rawTotal = Number(raw?.gwpTotal);
    if (Number.isFinite(rawTotal)) return rawTotal;
    const stageObj = modules || fallbackModules;
    if (!stageObj || typeof stageObj !== "object") {
      return Object.values(normalized.gwp || {}).reduce((sum, val) => sum + safeNumber(val), 0);
    }
    return Object.values(stageObj).reduce((sum, val) => sum + safeNumber(val), 0);
  })();

  return {
    ...normalized,
    uuid: uuid || id,
    declaredUnitMeta,
    modules: modules || fallbackModules,
    materialProperties,
    gwpTotal
  };
}

const PREVIEW_BAR_HEIGHT = 120;
const PALETTE = [
  "#FBCFE8",
  "#D1FAE5",
  "#BFDBFE",
  "#FDE68A",
  "#C7D2FE",
  "#FCA5A5",
  "#A7F3D0",
  "#E9D5FF",
  "#FCD34D",
  "#BFEDE3",
  "#F9A8D4",
  "#E5E7EB"
];

function defaultColor(i) {
  return PALETTE[i % PALETTE.length];
}

function formatImpact(value) {
  if (!Number.isFinite(value)) return "-";
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs === 0) return "0";
  if (abs < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function getModulesEntries(modules) {
  if (!modules) return [];
  if (Array.isArray(modules)) {
    return modules.map((m) => [m.key ?? m.module ?? m.name ?? "", m.value ?? m.total ?? 0]);
  }
  if (typeof modules === "object") {
    return Object.entries(modules);
  }
  return [];
}

const MODULE_ORDER = [
  "A1",
  "A2",
  "A3",
  "A1-A3",
  "A4",
  "A5",
  "B1",
  "B2",
  "B3",
  "B4",
  "B5",
  "B6",
  "B7",
  "C1",
  "C2",
  "C3",
  "C4",
  "C1-C4",
  "D"
];

function sortModuleEntries(entries) {
  const weight = (key) => {
    const idx = MODULE_ORDER.findIndex((k) => k.toLowerCase() === String(key).toLowerCase());
    return idx === -1 ? 999 : idx;
  };
  return [...entries].filter(([k]) => String(k).trim()).sort((a, b) => weight(a[0]) - weight(b[0]));
}

const ModulesGrid = ({ modules }) => {
  const entries = sortModuleEntries(getModulesEntries(modules));
  if (!entries.length) {
    return <div className="text-xs text-[#4f6b5a]">No stage data provided.</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="rounded border border-[#d5dacc] bg-white px-2 py-1 text-[11px] flex items-center justify-between"
        >
          <span className="font-medium text-[#2b4331]">{key}</span>
          <span className="text-[#2b4331]">{formatImpact(Number(value))}</span>
        </div>
      ))}
    </div>
  );
};

function getPropsMap(materialProperties) {
  if (!materialProperties) return null;
  if (materialProperties.properties && typeof materialProperties.properties === "object") {
    return materialProperties.properties;
  }
  if (typeof materialProperties === "object") return materialProperties;
  return null;
}

function pickProperty(propsMap, keys) {
  if (!propsMap || typeof propsMap !== "object") return null;
  const entries = Object.entries(propsMap);
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase() === target.toLowerCase());
    if (found) return { key: found[0], value: found[1] };
  }
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase().includes(target.toLowerCase()));
    if (found) return { key: found[0], value: found[1] };
  }
  return null;
}

function renderPropValue(entry) {
  if (entry == null) return "-";
  if (typeof entry === "number") return entry.toLocaleString();
  if (typeof entry === "string") return entry;
  if (typeof entry === "object") return formatProperty(entry);
  return String(entry);
}

function formatThickness(cm) {
  if (!Number.isFinite(cm)) return "-";
  if (cm < 10) return `${cm.toFixed(1)} cm`;
  return `${Math.round(cm)} cm`;
}

const WallSectionPreview = ({ wall }) => {
  const layers = wall?.layers || [];
  const positive = layers
    .map((layer, i) => ({ ...layer, color: layer.color || defaultColor(i) }))
    .filter((layer) => Number.isFinite(layer.thicknessCm) && layer.thicknessCm > 0);
  const total = positive.reduce((acc, layer) => acc + layer.thicknessCm, 0);

  const segments = useMemo(() => {
    if (total <= 0) return [];
    const raw = positive.map((layer) => ({ id: layer.id, w: (layer.thicknessCm / total) * 100 }));
    const min = 3;
    const withMin = raw.map((r) => ({ ...r, w: Math.max(min, r.w) }));
    if (withMin.length) {
      const totalWidth = withMin.reduce((a, r) => a + r.w, 0);
      const factor = 100 / totalWidth;
      withMin.forEach((r) => (r.w = r.w * factor));
    }
    return positive.map((layer, idx) => ({ ...layer, widthPct: withMin[idx].w }));
  }, [positive, total]);

  return (
    <div className="space-y-3">
      <div className="text-center">
        <div className="text-sm font-semibold text-[#2b4331]">Wall section preview</div>
        <div className="text-xs text-[#4f6b5a]">
          Total thickness: <span className="font-medium text-[#2b4331]">{Math.round(total)} cm</span>
        </div>
      </div>

      {total <= 0 ? (
        <div className="text-xs text-[#4f6b5a] bg-[#eef2e7] border border-dashed border-[#d5dacc] rounded-lg p-3 text-center">
          Add layer thicknesses &gt; 0 cm to see a scaled section.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-between text-[11px] text-[#4f6b5a] select-none px-2">
            <span>Outside</span>
            <span>Inside</span>
          </div>
          <div
            className="relative w-full bg-white overflow-hidden rounded-lg border border-[#d5dacc]"
            style={{ height: PREVIEW_BAR_HEIGHT }}
          >
            <div className="h-full w-full flex items-stretch">
              {segments.map((layer, idx) => (
                <div
                  key={layer.id || idx}
                  className="relative h-full border-r border-white/60 last:border-r-0"
                  style={{ width: `${layer.widthPct}%`, background: layer.color }}
                  title={`${layer.name || "Layer"} - ${formatThickness(layer.thicknessCm)}`}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/85 text-[10px] font-medium text-gray-800 shadow-sm">
                      {idx + 1}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[#d5dacc] pt-2">
            <div className="text-[11px] font-semibold text-[#2b4331] mb-2">Legend</div>
            <div className="grid grid-cols-1 gap-2">
              {layers.map((layer, idx) => (
                <div key={layer.id || idx} className="flex items-center gap-2 text-xs text-[#4f6b5a]">
                  <span
                    className="inline-block h-3 w-3 rounded-sm border"
                    style={{ background: layer.color || defaultColor(idx), borderColor: "#d5dacc" }}
                  />
                  <span className="min-w-[16px] text-[#2b4331] font-medium">{idx + 1}.</span>
                  <span className="flex-1 truncate">{layer.name || `Layer ${idx + 1}`}</span>
                  <span className="text-[#2b4331]">{formatThickness(layer.thicknessCm)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wall buildup library panel starts here.


const SavedWallBuildups = () => {
  const { savedWalls } = useWallBuildups();
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!savedWalls.length) {
      if (activeId !== null) setActiveId(null);
      return;
    }
    if (activeId && !savedWalls.some((wall) => wall.id === activeId)) {
      setActiveId(null);
    }
  }, [savedWalls, activeId]);

  const activeWall = useMemo(
    () => savedWalls.find((wall) => wall.id === activeId) || null,
    [savedWalls, activeId]
  );

  return (
    <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl min-h-[420px] max-h-[70vh] flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="text-[#2b4331]">Created wall buildups</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3 flex-1 overflow-y-auto">
        {savedWalls.length === 0 ? (
          <div className="text-sm text-[#4f6b5a]">
            No saved wall buildups yet. Create wall sections in the 'Buildups and components' tab in dashboard to view here and use it for the LCA.
          </div>
        ) : (
          <div className="space-y-2">
            {savedWalls.map((wall, idx) => {
              const isActive = wall.id === activeId;
              return (
                <div key={wall.id || idx} className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setActiveId((prev) => (prev === wall.id ? null : wall.id))}
                    className={`w-full text-left rounded-xl border px-3 py-1.5 transition ${
                      isActive
                        ? "border-[#2f6046] bg-[#f3f7ef]"
                        : "border-[#d5dacc] bg-white hover:border-[#a8b2a1]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#2b4331] truncate">
                          {wall.name || `Wall ${idx + 1}`}
                        </div>
                      </div>
                      <div className="text-right flex items-baseline justify-end gap-1 text-sm font-semibold text-[#2b4331]">
                        <span>{formatImpact(Number(wall.totalGwpPerM2))}</span>
                        <span className="text-[10px] text-[#4f6b5a]">
                          kgCO<sub>2</sub>e/m<sup>2</sup>
                        </span>
                      </div>
                    </div>
                  </button>

                  {isActive ? (
                    <div className="rounded-xl border border-[#d5dacc] bg-[#fbfcfa] p-3">
                      <WallSectionPreview wall={wall} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function LibraryPanel({
  epdLibrary,
  setEpdLibrary
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [nameOverflow, setNameOverflow] = useState({});
  const [geoOverflow, setGeoOverflow] = useState({});

  const filtered = useMemo(() => {
    const q = (filter || "").toLowerCase();
    if (!q) return epdLibrary || [];
    return (epdLibrary || []).filter((e) => {
      const s = `${e.name} ${e.source} ${e.declaredUnit}`.toLowerCase();
      return s.includes(q);
    });
  }, [epdLibrary, filter]);

  const onShortlist = (rawEpd) => {
    if (rawEpd?.__remove) {
      const uuid = rawEpd?.uuid || rawEpd?.id;
      if (!uuid) return;
      setEpdLibrary((prev) => (prev || []).filter((x) => String(x?.uuid || x?.id) !== String(uuid)));
      return;
    }
    const normalized = mapRawEpdToNormalized(rawEpd);
    setEpdLibrary((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.some((x) => x.id === normalized.id || x.uuid === normalized.uuid)) return arr;
      return [normalized, ...arr];
    });
  };

  const removeEpd = (id) => {
    setEpdLibrary((prev) => (prev || []).filter((x) => x.id !== id));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl min-h-[420px] max-h-[70vh] flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="w-4 h-4 text-[#2f6046]" />
            <span className="text-[#2b4331]">EPD library</span>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3 flex-1 overflow-y-auto">
          <div className="flex items-center gap-2">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter shortlisted EPDs"
              className="h-8"
            />
            <Button
              type="button"
              className="h-8 bg-[#2f6046] text-white hover:bg-[#25503a]"
              onClick={() => setOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="space-y-1.5">
            {(filtered || []).length === 0 ? (
              <div className="text-sm text-[#4f6b5a]">
                No shortlisted EPDs yet. Click Add to shortlist.
              </div>
            ) : (
              (() => {
                const items = filtered || [];
                const shortlistedItems = items.filter((e) => e.source !== "custom");
                const createdItems = items.filter((e) => e.source === "custom");
                const metaTextClass = "text-xs text-[#4f6b5a]";

                const renderRow = (e) => {
                  const geography = (e.geography || "").trim();
                  const isConfirmOpen = confirmRemoveId === e.id;
                  const isExpanded = !!expandedIds[e.id];
                  const duMeta = e.declaredUnitMeta;
                  const declaredLabel = duMeta
                    ? `${duMeta.amount ?? 1} ${duMeta.unit || ""}`.trim()
                    : e.declaredUnit || "-";
                  const modules = e.modules || {
                    "A1-A3": e.gwp?.A1A3,
                    A4: e.gwp?.A4,
                    A5: e.gwp?.A5,
                    B4: e.gwp?.B4,
                    "C1-C4": e.gwp?.C1C4,
                    D: e.gwp?.D
                  };
                  return (
                  <div key={e.id} className="space-y-2">
                    <div className="flex items-center justify-between rounded-xl border border-[#d5dacc] bg-white px-3 py-1 gap-3 min-h-[44px]">
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          className="w-full text-left min-w-0 overflow-hidden"
                          onClick={() =>
                            setExpandedIds((prev) => ({ ...prev, [e.id]: !prev[e.id] }))
                          }
                        >
                          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                            <TooltipProvider>
                              {nameOverflow[e.id] ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      className="text-sm font-semibold text-[#2b4331] truncate leading-tight min-w-0 flex-1"
                                      onMouseEnter={(event) => {
                                        const el = event.currentTarget;
                                        const isOverflow = el.scrollWidth > el.clientWidth;
                                        setNameOverflow((prev) =>
                                          prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                        );
                                      }}
                                      onFocus={(event) => {
                                        const el = event.currentTarget;
                                        const isOverflow = el.scrollWidth > el.clientWidth;
                                        setNameOverflow((prev) =>
                                          prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                        );
                                      }}
                                    >
                                      {e.name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-white text-[#2b4331] border border-[#d5dacc] shadow-md max-w-xs">
                                    {e.name}
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span
                                  className="text-sm font-semibold text-[#2b4331] truncate leading-tight min-w-0 flex-1"
                                  onMouseEnter={(event) => {
                                    const el = event.currentTarget;
                                    const isOverflow = el.scrollWidth > el.clientWidth;
                                    setNameOverflow((prev) =>
                                      prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                    );
                                  }}
                                  onFocus={(event) => {
                                    const el = event.currentTarget;
                                    const isOverflow = el.scrollWidth > el.clientWidth;
                                    setNameOverflow((prev) =>
                                      prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                    );
                                  }}
                                >
                                  {e.name}
                                </span>
                              )}
                            </TooltipProvider>
                            <div className={`flex items-center gap-1 shrink-0 ${metaTextClass}`}>
                              <span className="whitespace-nowrap">{e.declaredUnit}</span>
                              {geography ? <span className="text-[#9aa69a]">|</span> : null}
                              {geography ? (
                                <span
                                  className="max-w-[140px] truncate"
                                  title={geoOverflow[e.id] ? geography : undefined}
                                  onMouseEnter={(event) => {
                                    const el = event.currentTarget;
                                    const isOverflow = el.scrollWidth > el.clientWidth;
                                    setGeoOverflow((prev) =>
                                      prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                    );
                                  }}
                                  onFocus={(event) => {
                                    const el = event.currentTarget;
                                    const isOverflow = el.scrollWidth > el.clientWidth;
                                    setGeoOverflow((prev) =>
                                      prev[e.id] === isOverflow ? prev : { ...prev, [e.id]: isOverflow }
                                    );
                                  }}
                                >
                                  {geography}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </button>
                      </div>

                      <Popover
                        open={isConfirmOpen}
                        onOpenChange={(nextOpen) => setConfirmRemoveId(nextOpen ? e.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4 text-[#4f6b5a]" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="top"
                          align="center"
                          sideOffset={6}
                          className="w-auto border border-[#d5dacc] bg-white text-[#2b4331] shadow-md px-2 py-1.5"
                        >
                          <div className="flex items-center gap-2 text-xs font-semibold">
                            <span>Confirm?</span>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-[#2b4331] hover:bg-transparent hover:text-[#1f3528]"
                              onClick={() => setConfirmRemoveId(null)}
                              aria-label="Cancel delete"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-[#2f6046] hover:bg-transparent hover:text-[#214332]"
                              onClick={() => {
                                removeEpd(e.id);
                                setConfirmRemoveId(null);
                              }}
                              aria-label="Confirm delete"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {isExpanded ? (
                      <div className="rounded-xl border border-[#d5dacc] bg-[#f6f8f3] p-3">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#4f6b5a] mb-3">
                          <span className="font-semibold text-[#2b4331]">Declared unit:</span>
                          <span>{declaredLabel}</span>
                          <span className="font-semibold text-[#2b4331]">Total impact:</span>
                          <span>{formatImpact(Number(e.gwpTotal))}</span>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4f6b5a] mb-2">
                              Life-cycle stages
                            </div>
                            <ModulesGrid modules={modules} />
                          </div>
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4f6b5a] mb-2">
                              Material properties
                            </div>
                            <div className="space-y-2 text-xs">
                              {(() => {
                                const propsMap = getPropsMap(e.materialProperties);
                                if (!propsMap || Object.keys(propsMap).length === 0) {
                                  return (
                                    <div className="text-[#4f6b5a]">
                                      No material properties were provided.
                                    </div>
                                  );
                                }

                                const density = pickProperty(propsMap, ["bulkDensity", "grossDensity", "density", "densityKgPerM3"]);
                                const thickness = pickProperty(propsMap, ["layerThickness", "thickness", "thicknessM"]);
                                let grammage = pickProperty(propsMap, ["surfaceWeight", "massPerAreaKgPerM2"]);
                                if (!grammage) {
                                  const entries = Object.entries(propsMap);
                                  const guess = entries.find(([k]) => {
                                    const kl = k.toLowerCase();
                                    return (
                                      kl.includes("surfaceweight") ||
                                      (kl.includes("surface") && kl.includes("weight")) ||
                                      kl.includes("areal") ||
                                      kl.includes("areic") ||
                                      kl.includes("areadensity") ||
                                      kl.includes("areal density") ||
                                      kl.includes("areal_density")
                                    );
                                  });
                                  if (guess) grammage = { key: guess[0], value: guess[1] };
                                }

                                const rows = [
                                  density && { label: "Density", value: density.value },
                                  thickness && { label: "Thickness", value: thickness.value },
                                  grammage && { label: "Grammage", value: grammage.value }
                                ].filter(Boolean);

                                if (rows.length === 0) {
                                  return (
                                    <div className="text-[#4f6b5a]">
                                      No material properties were provided.
                                    </div>
                                  );
                                }

                                return rows.map((row, i) => (
                                  <div
                                    key={i}
                                    className="flex items-center justify-between rounded border border-[#d5dacc] bg-white px-2 py-1"
                                  >
                                    <span className="text-[#2b4331]">{row.label}</span>
                                    <span className="text-[#2b4331]">{renderPropValue(row.value)}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
                };

                return (
                  <div className="space-y-1.5">
                    {shortlistedItems.length > 0 && (
                      <>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4f6b5a]">
                          Shortlisted
                        </div>
                        {shortlistedItems.map(renderRow)}
                      </>
                    )}

                    {shortlistedItems.length > 0 && createdItems.length > 0 && (
                      <div className="border-t border-[#d5dacc] my-1" />
                    )}

                    {createdItems.length > 0 && (
                      <>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#4f6b5a]">
                          Created
                        </div>
                        {createdItems.map(renderRow)}
                      </>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-7xl max-h-[94vh] overflow-y-auto bg-white text-[#2b4331]">
              <DialogHeader className="text-left">
                <DialogTitle className="text-[#2b4331]">Shortlist EPDs</DialogTitle>
                <DialogDescription className="text-[#4f6b5a]">
                  Use your existing database explorer to shortlist EPDs into this LCA session.
                </DialogDescription>
              </DialogHeader>

              <div className="pt-2">
                <MaterialDatabaseExplorer
                  onShortlist={(epd) => onShortlist(epd)}
                  shortlist={epdLibrary || []}
                />
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      <SavedWallBuildups />
    </div>
  );
}
